import { ColorContext, LayoutSizeContext, UniqueWidgetRefreshContext } from 'App';
import { DeviceControls, FusionDropDownInput } from 'components/index';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import { DeviceContext } from 'features/integrations/contexts/DeviceContext';
import DeviceMap from 'features/integrations/features/ConnectedDevices/features/DeviceMap/index.web.jsx';
import { selectIntegrationFetching } from 'features/integrations/state/selectors/integrationSelectors';
import {
  DeviceIsDeviceType,
  DeviceIsSystemType,
} from 'features/integrations/utils/deviceComparisionUtils';
import Cookies from 'js-cookie';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { sendDeviceCommand } from 'services/devices/commands/commands.service';
import { useGetConnectedDevicesQuery } from 'services/devices/devices.api';
import { ConnectionsContext } from './Connections';
import DeviceHistory from './DeviceSettings/DeviceHistory';

export default function RescAlertDevicesWidget({
  type = '',
  dimensions,
  nested = false,
  deviceForWidget = null,
}) {
  const connectionsContext = useContext(ConnectionsContext);
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const fetching = useSelector(selectIntegrationFetching);
  const deviceContext = useContext(DeviceContext);
  const layout = useContext(LayoutSizeContext);
  const [selectedDevice, setSelectedDeviceIndex] = useState(0);
  const [startTime, setStartTime] = useState(new Date().getTime() - 24 * 60 * 60 * 1000);
  const [endTime, setEndTime] = useState(new Date().getTime());
  const [updatedSelectedDevice, setUpdatedSelectedDevice] = useState(false);
  const [pageIndex, setPageIndex] = useState(1);
  const [showHistory, setShowHistory] = useState(false);
  const [viewImageInWidget, setViewImageInWidget] = useState(false);
  const [mapWidth, setMapWidth] = useState(501);
  const [mapHeight, setMapHeight] = useState(451);
  const [customDataOverride, setCustomDataOverride] = useState(null);
  const theme = useContext(ColorContext);
  const [useThisDevice, setUseThisDevice] = useState(deviceForWidget || false);
  const [devicesLoaded, setDevicesLoaded] = useState(false);

  // Minimum width (in px) for the DeviceControls area to keep controls usable
  const controlsMinWidth = 320;

  const setSelectedDevice = (index) => {
    setSelectedDeviceIndex(index);
    setUpdatedSelectedDevice(!updatedSelectedDevice);
  };

  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [displayDevice, setDisplayDevice] = useState(useThisDevice);
  const [initialized, setInitialized] = useState(false);
  const [sending, setSending] = useState(false);
  const [displayMessage, setDisplayMessage] = useState();
  const [audioOverride, setAudioOverride] = useState(null);
  const [audioManager, setAudioManager] = useState(false);
  const [widgetSelectedIndex, setWidgetSelectedIndex] = useState(-1);

  useEffect(() => {
    setDisplayDevice(useThisDevice);
  }, [useThisDevice]);

  const deviceControlListener = async (evt, data) => {
    setSuccessMsg('');
    setErrorMsg('');

    switch (evt) {
      case 'update':
        setDisplayDevice(data);
        setInitialized(true);
        return;
      case 'submit':
        setSending(true);
        try {
          console.log('renderIntervalInput data:', data);

          const {
            id,
            node_id,
            system_type,
            device_type,
            status,
            maintenanceOverride,
            comments,
            interval,
            volume,
            repeat,
            audio,
          } = data;

          await sendDeviceCommand(
            { id, node_id, system_type, device_type },
            status === -1 ? 1 : status,
            comments,
            maintenanceOverride || false,
            {
              interval: interval || 0,
              volume,
              repeat,
              audio,
              immediate: status === -1,
            },
          );
          setSuccessMsg('Command successfully sent');
        } catch (error) {
          setErrorMsg(error.message);
        }
        setSending(false);
        return;
      case 'cancel-audio':
        try {
          sendDeviceCommand(
            {
              id: useThisDevice.id,
              node_id: useThisDevice.node_id,
              system_type: useThisDevice.system_type,
              device_type: useThisDevice.device_type,
            },
            7,
            '',
            false,
          );
        } catch (err) {
          console.log('Error sending device command:', err.message);
          setDisplayMessage({
            type: 'error',
            message: 'Failed to send command!',
          });
        }
        return;
      case 'audio-manager':
        console.log('Opening audio manager');
        setAudioManager(true);
        return;
      default:
        return;
    }
  };

  const uwf = useContext(UniqueWidgetRefreshContext);
  const ROUTE = 'getNodes';
  const LOCATION = Cookies.get('city') + '-' + Cookies.get('state');
  const UNIQUEKEY = LOCATION + '-' + ROUTE;
  const deviceTypeToFilterToType = (deviceType) => {
    let t;
    if (DeviceIsDeviceType({ device_type: deviceType }, 'Stage')) {
      t = 'Stage';
    }
    if (deviceType === 'manualRoadClosure') {
      t = 'Manual Road Closure';
    }
    if (deviceType === 'technologyBridge') {
      t = 'Technology Bridge_Flashing Lights';
    }
    if (deviceType === 'technologyBridgeSiren') {
      t = 'Technology Bridge_Siren/Giant Voice';
    }
    return t;
  };

  const isAwareFlood = type === 'stage';
  const widgetDeviceType = useMemo(() => deviceTypeToFilterToType(type), [type]);
  const widgetDevices = useMemo(() => {
    if (!connectedDevices?.length || !widgetDeviceType) return [];
    return connectedDevices
      .map((device, index) => ({ device, originalIndex: index }))
      .filter(
        ({ device }) =>
          device.device_type === widgetDeviceType &&
          (!isAwareFlood || DeviceIsSystemType({ system_type: device.system_type }, 'Flood')) &&
          connectionsContext.canViewDeviceType(device),
      )
      .sort((a, b) => parseInt(a.device.node_id || 0, 10) - parseInt(b.device.node_id || 0, 10));
  }, [connectedDevices, connectionsContext, widgetDeviceType]);
  const widgetDeviceOptions = useMemo(
    () =>
      widgetDevices.map(
        ({ device }) =>
          (device.device_type !== 'Manual Road Closure' ? device.static_id + ' ' : '') +
          device.alias,
      ),
    [widgetDevices],
  );
  const handleWidgetDeviceChange = (value) => {
    const index = Number(value);
    if (!Number.isFinite(index) || index < 0) return;
    const selected = widgetDevices[index];
    if (!selected) return;
    setWidgetSelectedIndex(index);
    setSelectedDevice(selected.originalIndex);
    setUseThisDevice(selected.device);
  };

  useEffect(() => {
    if (!connectedDevices) return;

    if (!connectedDevices[selectedDevice]?.data) {
      return;
    }
    deviceContext.getDeviceData(startTime, endTime);
  }, [uwf[UNIQUEKEY]]);

  useEffect(() => {
    if (!connectedDevices) {
      return;
    }
    if (devicesLoaded) return;
    if (deviceForWidget) {
      const foundIndex = connectedDevices.findIndex((device) => device.id === deviceForWidget.id);
      if (foundIndex !== -1) {
        setSelectedDevice(foundIndex);
      } else {
        console.error('Device not found in context for widget:', deviceForWidget);
      }
      return;
    }

    //find first node of type, then set selectedDevice to that index
    let dtype = '';
    if (type === 'stage') {
      dtype = 'Stage';
    }
    if (type === 'manualRoadClosure') {
      dtype = 'Manual Road Closure';
    }
    if (type === 'technologyBridge') {
      dtype = 'Technology Bridge_Flashing Lights';
    }
    if (type === 'technologyBridgeSiren') {
      dtype = 'Technology Bridge_Siren/Giant Voice';
    }

    const firstNodeOfType = connectedDevices.findIndex((device) => {
      return (
        device.device_type === dtype &&
        (!isAwareFlood || DeviceIsSystemType({ system_type: device.system_type }, 'Flood'))
      );
    });

    if (firstNodeOfType != -1) setSelectedDevice(firstNodeOfType);
    //find node by id
    if (firstNodeOfType !== -1) {
      const nodeById = connectedDevices[firstNodeOfType];
      if (nodeById) {
        setUseThisDevice(nodeById);
        console.log('hard setting device to:', nodeById);
      }
    }

    if (connectedDevices.length > 0) setDevicesLoaded(true);
  }, [connectedDevices]);

  useEffect(() => {
    if (!widgetDevices.length) {
      setWidgetSelectedIndex(-1);
      return;
    }
    const selectedIndex = widgetDevices.findIndex(({ device }) => device.id === useThisDevice?.id);
    if (selectedIndex !== -1) {
      setWidgetSelectedIndex(selectedIndex);
    }
  }, [useThisDevice, widgetDevices]);

  if (fetching) {
    return 'fetching...';
  }

  if (fetching) {
    return (
      <div
        style={{
          height: '100%',
        }}
      >
        <WidgetLoadingAnimation widgetName="DevicesMap" dimensions={dimensions} />
      </div>
    );
  }

  const renderDeviceData = (size) => {
    return (
      <DeviceHistory
        device={connectedDevices[selectedDevice]}
        selectedDevice={selectedDevice}
        widget={true}
        size={size}
        setShowHistory={setShowHistory}
      />
    );
  };

  const renderWidgetMap = (sidebarOpen) => {
    const hasVisibleWidgetDevices = widgetDevices.length > 0;

    if (dimensions[0] === 2 && dimensions[1] === 1) {
      return (
        <div>
          {!nested && <WidgetTimeUpdate route={ROUTE} realtime />}
          <div
            style={{
              height: layout.widgetHeight - (nested ? 0 : 30),
            }}
          >
            {showHistory ? (
              <div>{renderDeviceData('2x1')}</div>
            ) : (
              <div style={{ display: 'flex', height: '100%', overflow: 'hidden', gap: 12 }}>
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: '100%',
                  }}
                >
                  <DeviceMap
                    deviceTypeToFilter={type}
                    setUseThisDevice={setUseThisDevice}
                    setShowCurrentDevice={() => {}}
                    setShowDeviceSettings={() => {}}
                    filtersCompact={dimensions?.[0] === 1 && dimensions?.[1] === 2}
                    enableFilters={false}
                    selectedDeviceOverride={useThisDevice}
                    widget
                  />
                </div>

                <div
                  style={{
                    minWidth: controlsMinWidth,
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    height: '100%',
                    overflow: 'visible',
                  }}
                >
                  <div
                    style={{
                      position: 'relative',
                      zIndex: 20,
                      flexShrink: 0,
                      marginBottom: 8,
                    }}
                  >
                    <FusionDropDownInput
                      label="Device"
                      options={widgetDeviceOptions}
                      value={widgetSelectedIndex}
                      onChange={handleWidgetDeviceChange}
                      disabled={widgetDeviceOptions.length === 0}
                    />
                  </div>

                  <div
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}
                  >
                    {hasVisibleWidgetDevices ? (
                      <DeviceControls
                        device={displayDevice}
                        displayMessage={displayMessage}
                        on={deviceControlListener}
                        sending={sending}
                        audioOverride={audioOverride}
                        successMsg={successMsg}
                        errorMsg={errorMsg}
                        audioManagerButtonEnabled={false}
                      />
                    ) : (
                      <div>No Devices Found</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }

    if ((dimensions?.[1] || 0) >= 2) {
      return (
        <div className="h-100 w-100 d-flex flex-column">
          {!nested && <WidgetTimeUpdate route={ROUTE} realtime />}
          <div className="w-100 h-100">
            {showHistory ? (
              <div>{renderDeviceData('1x2')}</div>
            ) : (
              <div className="h-100 w-100 d-flex flex-column">
                <div className="ratio ratio-4x3 w-100 flex-shrink-0">
                  <div className="rounded-3 overflow-hidden">
                    <DeviceMap
                      deviceTypeToFilter={type}
                      setUseThisDevice={setUseThisDevice}
                      setShowCurrentDevice={() => {}}
                      setShowDeviceSettings={() => {}}
                      filtersCompact={dimensions?.[0] === 1 && dimensions?.[1] === 2}
                      enableFilters={false}
                      selectedDeviceOverride={useThisDevice}
                      widget
                    />
                  </div>
                </div>

                <div className="mx-3 mt-2" style={{ position: 'relative', zIndex: 20 }}>
                  <FusionDropDownInput
                    label="Device"
                    options={widgetDeviceOptions}
                    value={widgetSelectedIndex}
                    onChange={handleWidgetDeviceChange}
                    disabled={widgetDeviceOptions.length === 0}
                  />
                </div>

                <div className="d-flex flex-column m-3 flex-grow-1" style={{ minHeight: 0 }}>
                  {hasVisibleWidgetDevices ? (
                    <DeviceControls
                      device={displayDevice}
                      displayMessage={displayMessage}
                      on={deviceControlListener}
                      sending={sending}
                      audioOverride={audioOverride}
                      successMsg={successMsg}
                      errorMsg={errorMsg}
                      audioManagerButtonEnabled={false}
                    />
                  ) : (
                    <div>No Devices Found</div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    }
  };

  if (connectedDevices.length > 0) {
    return renderWidgetMap();
  }

  return 'Loading...';
  return <WidgetLoadingAnimation />;
}
